/**
 * Home — signed-in dashboard + profile-editor surface. Renders inside
 * AppShell.
 *
 * Layout:
 *   - No greeting topbar — the YOUR CARD row + the right-column
 *     ScoreHero on desktop carry the screen's header rhythm. The
 *     Share CTA used to live in the topbar; it's now an overlay
 *     button on the share-card preview (right column on desktop,
 *     coral chip in the YOUR CARD row on mobile).
 *   - LEFT column (1.45fr): everything that used to live on /profile
 *     — 4:5 photo carousel with name overlay, identity chips, About,
 *     Music, "Your public link" section with Copy + View as public,
 *     Edit button (deep-link to /builder).
 *   - RIGHT column (0.95fr): ScoreHero (the rating-summary card) on
 *     top, then ShareCardPreview (with a small overlay Share control
 *     for the user — note: the SOCIAL share image generated server-
 *     side does NOT include this button; it's a viewer-only chrome),
 *     then PremiumPitch when on the free tier.
 *
 * The /profile route was deleted; bookmarks fall through to /home
 * which now shows the same content.
 *
 * Empty states:
 *   - Not published: profile-preview column shows a publish CTA in
 *     place of the photos; right column hides ScoreHero (no ratings
 *     before publish) but still shows the share-card preview blocked
 *     out as "Publish to share".
 */
import { useEffect, useRef, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import { toPng } from 'html-to-image';
import {
  api, mediaApi, profilesApi, publicApi, ratingsApi,
  type Profile, type PublicProfile, type RatingsDashboard, type User,
} from '@/lib/api';
import { registerForPushAsync } from '@/lib/push';
import {
  Btn, Chip, Eyebrow, Icons, Photo, Spinner, photoVariant,
} from '@/components';
import { MusicSplit } from '@/components/MusicCards';
import { PaywallSheet } from '@/components/PaywallSheet';
import { friendlyError } from '@/lib/errors';

export function HomePage() {
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [ratings, setRatings] = useState<RatingsDashboard | null>(null);
  // Public snapshot — sourced for photos + bio + music + age in one
  // shot. Same data /p/:slug serves to raters.
  const [publicSnap, setPublicSnap] = useState<PublicProfile | null>(null);
  // Share-card overlay — opened by the Share button in the "Your card"
  // row. Renders the same ShareCardPreview the right column shows on
  // desktop, but inside a modal scrim. On mobile the right-column
  // inline preview is hidden via .home-share-card so the modal is the
  // only way to view + share the card.
  const [shareOpen, setShareOpen] = useState(false);
  const [paywallOpen, setPaywallOpen] = useState(false);
  useEffect(() => {
    api.me().then(setUser).catch(() => null);
    profilesApi.me().then(setProfile).catch(() => null);
    ratingsApi.dashboard().then(setRatings).catch(() => null);
    registerForPushAsync().catch(() => null);
  }, []);

  useEffect(() => {
    // Skip fetch when private — the backend returns 404 for private
    // slugs even to the owner via the public route. The previously
    // loaded snap stays in state so the owner can still preview the
    // card after toggling private; only a hard refresh while private
    // shows the "your card is private" lockup.
    if (!profile?.slug || !profile.published || profile.visibility === 'private') return;
    publicApi.getProfile(profile.slug).then(setPublicSnap).catch(() => null);
  }, [profile?.slug, profile?.published, profile?.visibility]);

  // Privacy toggle handler — optimistic local update + setVisibility
  // API call + revert on failure. Owner-only; treats 'link_only' as
  // public (the two-state UX collapses link_only onto public).
  const onToggleVisibility = async (makePublic: boolean) => {
    if (!profile) return;
    const next = makePublic ? 'public' : 'private';
    const prev = profile.visibility;
    setProfile({ ...profile, visibility: next });
    try {
      const saved = await profilesApi.setVisibility(next);
      setProfile(saved);
    } catch {
      setProfile({ ...profile, visibility: prev });
    }
  };

  // Unified loading gate — wait until we KNOW whether the profile is
  // published before rendering anything. Previously the body rendered
  // optimistically with published=false (defaulted from a null profile),
  // which painted the now-removed PublishPrompt for ~1 frame before
  // the real profile state arrived and swapped it out. That flicker
  // was the bug.
  if (!user || !profile) {
    return <CenteredSpinner />;
  }

  // Profile exists but isn't published yet → builder owns this state
  // (it's the only place where the publish action lives). Replace,
  // not push, so the back button skips /home rather than bouncing.
  if (!profile.published) {
    return <Navigate to="/builder" replace />;
  }

  // Published + public — wait for the public snap so ProfilePreview
  // renders with real photos instead of the "card is private" empty
  // state during the fetch. Private profiles skip the snap fetch by
  // design (backend 404s them), so we don't wait on it.
  if (profile.visibility !== 'private' && !publicSnap) {
    return <CenteredSpinner />;
  }

  const ratingCount = ratings?.rating_count ?? 0;
  const yesPercent = ratings?.yes_percent ?? 0;
  const weekDelta = (ratings?.recent ?? []).filter(isWithinDays(7)).length;
  const initial = (user.display_name || user.email)[0]?.toUpperCase() ?? '?';

  return (
    <>
      {/* No greeting topbar — the YOUR CARD row + the right-column
          ScoreHero on desktop, plus the dedicated /ratings tab, give
          the screen enough surface area. Removing the "Hi, <name>"
          eyebrow matches the RN mobile home shape on every breakpoint. */}

      {/* Mobile no longer shows ScoreHero — RN mobile doesn't either,
          and the dedicated /ratings tab carries the same numbers in
          more detail. Desktop renders ScoreHero inside the right
          column (wrapped in .hide-on-mobile there for parity). */}

      <div
        className="home-grid"
        style={{
          // 28px top pad aligns the YOUR CARD eyebrow + ScoreHero with
          // the sidebar's wydm wordmark baseline (sidebar padding-top
          // 24 + brand-link padding-top 4). Mobile override in base.css
          // resets this so phones don't gain extra empty space at the
          // top of the screen.
          padding: '28px 40px 60px',
          display: 'grid',
          gridTemplateColumns: 'minmax(0, 1.45fr) minmax(0, 0.95fr)',
          gap: 24,
          alignItems: 'flex-start',
        }}
      >
        {/* ─── LEFT COLUMN ─ profile content ─ */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          <ProfilePreview
            snap={publicSnap}
            isPublic={profile.visibility !== 'private'}
            onToggleVisibility={onToggleVisibility}
            onShare={() => setShareOpen(true)}
          />
        </div>

        {/* ─── RIGHT COLUMN ─ rating + share + premium ─
            Sticky on desktop (24px from the top of the viewport) so
            the ScoreHero + ShareCardPreview follow the user as they
            scroll the longer left column. The .home-right class
            unsticks on mobile via base.css (the column stacks into
            the main flow there). */}
        <div
          className="home-right"
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 16,
            position: 'sticky',
            top: 24,
            alignSelf: 'start',
          }}
        >
          {/* ScoreHero desktop slot — hidden on mobile because the
              .show-on-mobile copy above already renders it at the
              top, where it has more visual weight on a phone. */}
          <div className="hide-on-mobile">
            <ScoreHero pct={yesPercent} count={ratingCount} deltaWeek={weekDelta} />
          </div>

          {profile.slug && (
            <div className="home-share-card">
              <ShareCardPreview
                slug={profile.slug}
                publicUrl={profile.public_url ?? `https://wydm.app/p/${profile.slug}`}
                userId={user.id}
                initial={initial}
                photoUrl={publicSnap?.photos[0]?.url ?? null}
                displayName={user.display_name || user.email.split('@')[0]}
              />
            </div>
          )}

          {user.tier !== 'premium' && (
            <PremiumPitch onUpgrade={() => setPaywallOpen(true)} />
          )}
        </div>
      </div>

      <PaywallSheet
        open={paywallOpen}
        trigger="subscribe"
        onClose={() => setPaywallOpen(false)}
      />

      {/* Share-card overlay — opens via the Share button in the Your
          Card row. Mounts only when open so html-to-image's capture
          + the inlined-photo fetch don't run on every page load. */}
      {shareOpen && profile.slug && (
        <ShareCardOverlay
          onClose={() => setShareOpen(false)}
          slug={profile.slug}
          publicUrl={profile.public_url ?? `https://wydm.app/p/${profile.slug}`}
          userId={user.id}
          initial={initial}
          photoUrl={publicSnap?.photos[0]?.url ?? null}
          displayName={user.display_name || user.email.split('@')[0]}
        />
      )}
    </>
  );
}

// ShareCardOverlay — modal wrapper around ShareCardPreview. The
// preview already owns its own Share button + menu (Copy link / Share
// card), so the overlay is just a centring scrim + close affordance.
// On mobile this is the ONLY way to view/share the card (the inline
// right-column copy is hidden by .home-share-card's media rule). On
// desktop, both surfaces work — the modal mirrors what's on the right
// column for users who tap Share instead of using the inline button.
function ShareCardOverlay({
  onClose, slug, publicUrl, userId, initial, photoUrl, displayName,
}: {
  onClose: () => void;
  slug: string;
  publicUrl: string;
  userId: string;
  initial: string;
  photoUrl: string | null;
  displayName: string;
}) {
  // Close on Escape — modal pattern users expect.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Share your card"
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(43,29,20,0.55)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 100, padding: 20, animation: 'fade-in 0.2s ease',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: '100%', maxWidth: 400, position: 'relative',
          animation: 'scale-in 0.2s ease',
        }}
      >
        {/* White frame — gives the 1:1 card a "presented" surface
            against the dark scrim instead of letting it float
            edge-to-edge. Mirrors the RN ShareCardModal frame so the
            two surfaces look the same. */}
        <div
          style={{
            background: 'var(--surface)',
            borderRadius: 28,
            padding: 16,
            boxShadow: 'var(--shadow-lift)',
          }}
        >
          <ShareCardPreview
            slug={slug}
            publicUrl={publicUrl}
            userId={userId}
            initial={initial}
            photoUrl={photoUrl}
            displayName={displayName}
          />
          {/* Caption — display name beneath the card, inside the
              frame. Matches the RN modal's bottom caption. */}
          <div
            style={{
              marginTop: 12,
              textAlign: 'center',
              fontFamily: 'var(--font-ui)',
              fontSize: 13,
              color: 'var(--ink-2)',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {displayName}
          </div>
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          style={{
            position: 'absolute',
            top: -12,
            right: -12,
            width: 32,
            height: 32,
            borderRadius: 32,
            background: 'var(--ink)',
            color: '#fff',
            border: 'none',
            boxShadow: 'var(--shadow-lift)',
            cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 20, lineHeight: 1,
          }}
        >
          ×
        </button>
      </div>
    </div>
  );
}

// ── ProfilePreview — left-column "what raters see" surface ────────────
// Combines what /profile used to show (photo carousel, identity chips,
// about, music, public link) into a single scrollable column.
function ProfilePreview({
  snap, isPublic, onToggleVisibility, onShare,
}: {
  snap: PublicProfile | null;
  isPublic: boolean;
  onToggleVisibility: (next: boolean) => void | Promise<void>;
  onShare: () => void;
}) {
  const [photoIdx, setPhotoIdx] = useState(0);

  // Private + nothing cached → owner has no snap to render. Show the
  // controls + a soft empty state.
  if (!snap) {
    return (
      <>
        <ProfileHeader isPublic={isPublic} onToggleVisibility={onToggleVisibility} onShare={onShare} />
        <div
          style={{
            background: 'var(--surface)',
            borderRadius: 22,
            padding: 32,
            boxShadow: 'var(--shadow-card)',
            textAlign: 'center',
            color: 'var(--ink-3)',
          }}
        >
          <div className="serif" style={{ fontSize: 22, fontStyle: 'italic', fontWeight: 500, color: 'var(--ink)', marginBottom: 8 }}>
            Your card is private.
          </div>
          <p style={{ fontSize: 14, lineHeight: 1.5, margin: 0 }}>
            Anyone visiting <code style={{ fontFamily: 'var(--font-mono)' }}>wydm.app/p/&lt;you&gt;</code> gets a 404. Flip the
            switch above to make it visible again.
          </p>
        </div>
      </>
    );
  }

  const photo = snap.photos[photoIdx];
  const hasPhotos = snap.photos.length > 0;

  return (
    <>
      <ProfileHeader isPublic={isPublic} onToggleVisibility={onToggleVisibility} onShare={onShare} />

      {/* Photo carousel */}
      {hasPhotos ? (
        <div
          style={{
            position: 'relative',
            aspectRatio: '4/5',
            borderRadius: 28,
            overflow: 'hidden',
            boxShadow: 'var(--shadow-card)',
            background: 'var(--surface-2)',
          }}
        >
          <img
            src={photo.url}
            alt={`${snap.display_name} photo ${photoIdx + 1}`}
            style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
            draggable={false}
          />
          {/* Bottom vignette — same three-stop gradient as the share
              card. Gives the name + age lockup legible contrast over
              bright photos without darkening the top progress bars. */}
          <div
            style={{
              position: 'absolute',
              inset: 0,
              pointerEvents: 'none',
              background:
                'linear-gradient(to top, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0.3) 32%, transparent 60%)',
            }}
          />
          {snap.photos.length > 1 && (
            <div style={{ position: 'absolute', top: 18, left: 18, right: 18, display: 'flex', gap: 5 }}>
              {snap.photos.map((_, i) => (
                <div
                  key={i}
                  style={{
                    flex: 1,
                    height: 3,
                    borderRadius: 3,
                    background: i <= photoIdx ? '#fff' : 'rgba(255,255,255,0.3)',
                  }}
                />
              ))}
            </div>
          )}
          {snap.photos.length > 1 && (
            <>
              <CarouselBtn side="left" disabled={photoIdx === 0} onClick={() => setPhotoIdx((i) => Math.max(0, i - 1))} />
              <CarouselBtn side="right" disabled={photoIdx === snap.photos.length - 1} onClick={() => setPhotoIdx((i) => Math.min(snap.photos.length - 1, i + 1))} />
            </>
          )}
          <div style={{ position: 'absolute', left: 28, bottom: 28, color: '#fff', textShadow: '0 1px 2px rgba(0,0,0,0.3)' }}>
            <div className="serif pub-name" style={{ fontSize: 52, lineHeight: 1, fontWeight: 500, letterSpacing: -1 }}>
              {snap.display_name}
              {snap.age && <span style={{ fontStyle: 'italic', fontWeight: 400 }}>, {snap.age}</span>}
            </div>
          </div>
        </div>
      ) : (
        <div
          style={{
            aspectRatio: '4/5',
            borderRadius: 28,
            background: 'var(--surface-2)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'var(--ink-3)',
          }}
        >
          No photos
        </div>
      )}

      {/* Identity chips (gender + looking-for) — above About per the
          public-profile pattern, gives raters the key signal first. */}
      {(snap.gender || snap.looking_for.length > 0) && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          {snap.gender && <Chip>{capitalize(snap.gender)}</Chip>}
          {snap.looking_for.length > 0 && (
            <Chip>
              Looking for {snap.looking_for.map(capitalize).join(' · ').toLowerCase()}
            </Chip>
          )}
        </div>
      )}

      {/* About — bare, sits on the page bg. Section eyebrow + bio. */}
      <div>
        <Eyebrow letter={2} style={{ marginBottom: 12 }}>About</Eyebrow>
        {snap.bio ? (
          <div className="serif pub-bio" style={{ fontSize: 19, lineHeight: 1.5, letterSpacing: -0.1 }}>
            {snap.bio}
          </div>
        ) : (
          <div style={{ fontSize: 14, color: 'var(--ink-3)', fontStyle: 'italic' }}>
            You haven't written a bio yet.
          </div>
        )}
      </div>

      {/* Music — bare. MusicSplit renders 'On repeat' / 'Top artists'
          eyebrows per column, so no wrapper eyebrow needed when there's
          content. The empty state gets its own eyebrow + hint. */}
      {((snap.music?.length ?? 0) > 0 || (snap.music_artists?.length ?? 0) > 0) ? (
        <MusicSplit
          tracks={snap.music ?? []}
          artists={snap.music_artists ?? []}
          tracksLabel="On repeat"
          artistsLabel="Top artists"
        />
      ) : (
        <div>
          <Eyebrow letter={2} style={{ marginBottom: 12 }}>Music</Eyebrow>
          <div style={{ fontSize: 14, color: 'var(--ink-3)', fontStyle: 'italic' }}>
            Connect Spotify in the builder to show your top tracks + artists.
          </div>
        </div>
      )}

      {/* "Your public link" card removed — Copy link now lives in the
          share-card menu (right column) + View as public sits beneath
          the share-card preview. */}
    </>
  );
}

// ProfileHeader — eyebrow + privacy switch + Edit button. Shared by the
// "has snap" and "private (no snap)" branches of ProfilePreview so the
// owner always sees the same controls in the same place. View-public
// affordance now lives on the share card itself (eye icon next to the
// share button) on both desktop + mobile, so no extra eye icon here.
function ProfileHeader({
  isPublic, onToggleVisibility, onShare,
}: {
  isPublic: boolean;
  onToggleVisibility: (next: boolean) => void | Promise<void>;
  onShare: () => void;
}) {
  // "Your card" eyebrow dropped — the controls (toggle + chips) carry
  // the row on their own. justify-content flips to flex-end so the
  // cluster stays anchored to the right edge.
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 12, flexWrap: 'wrap' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <VisibilityToggle isPublic={isPublic} onChange={onToggleVisibility} />
        <Link to="/builder" style={{ textDecoration: 'none' }} aria-label="Edit profile">
          {/* The text label is hidden on mobile so the button shrinks
              to an icon-only chip. aria-label on the parent <Link>
              preserves the screen-reader name. */}
          <Btn variant="cream" size="sm" icon={<Icons.edit size={13} />}>
            <span className="hide-on-mobile">Edit</span>
          </Btn>
        </Link>
        {/* Share button — mobile-only. On desktop the inline
            ShareCardPreview already lives in the right column, so a
            second entry-point would be redundant. The .show-on-mobile
            wrapper is display:none above 900px (see base.css). Uses
            the Btn primary variant so the icon-only chip on mobile
            matches Edit's footprint pixel-for-pixel (sm = 36 tall,
            14px side padding). */}
        <span className="show-on-mobile">
          <Btn
            variant="primary"
            size="sm"
            icon={<Icons.share size={13} />}
            onClick={onShare}
            aria-label="Share my card"
          />
        </span>
      </div>
    </div>
  );
}

// VisibilityToggle — small two-state pill. "Public" lights coral; "Private"
// is the off state. Tap to flip. Optimistic: parent handles revert on
// failure. Keyboard-accessible via the underlying <button role="switch">.
function VisibilityToggle({
  isPublic, onChange,
}: { isPublic: boolean; onChange: (next: boolean) => void | Promise<void> }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={isPublic}
      onClick={() => onChange(!isPublic)}
      title={isPublic ? 'Card is public — anyone with the link can view it' : 'Card is private — public link returns 404'}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 8,
        padding: '5px 8px 5px 12px',
        borderRadius: 100,
        border: '1px solid var(--line-strong)',
        background: 'var(--surface)',
        color: 'var(--ink-2)',
        fontSize: 12,
        fontWeight: 600,
        cursor: 'pointer',
        fontFamily: 'var(--font-ui)',
      }}
    >
      {isPublic ? 'Public' : 'Private'}
      <span
        aria-hidden
        style={{
          position: 'relative',
          width: 30,
          height: 18,
          borderRadius: 100,
          background: isPublic ? 'var(--coral)' : 'var(--line-strong)',
          transition: 'background 0.15s',
        }}
      >
        <span
          style={{
            position: 'absolute',
            top: 2,
            left: isPublic ? 14 : 2,
            width: 14,
            height: 14,
            borderRadius: 14,
            background: '#fff',
            transition: 'left 0.15s',
            boxShadow: '0 1px 2px rgba(0,0,0,0.2)',
          }}
        />
      </span>
    </button>
  );
}


function CarouselBtn({ side, disabled, onClick }: { side: 'left' | 'right'; disabled: boolean; onClick: () => void }) {
  const ChevIcon = side === 'left' ? Icons.chevL : Icons.chevR;
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={side === 'left' ? 'Previous photo' : 'Next photo'}
      style={{
        position: 'absolute',
        [side]: 16,
        top: '50%',
        transform: 'translateY(-50%)',
        width: 44,
        height: 44,
        borderRadius: 44,
        background: 'rgba(255,255,255,0.92)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        opacity: disabled ? 0.35 : 1,
        cursor: disabled ? 'default' : 'pointer',
        boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
        border: 'none',
        color: 'var(--ink)',
      }}
    >
      <ChevIcon size={18} />
    </button>
  );
}

// ── Score hero (ink bg, big italic coral %, ring chart) ─────────────────
function ScoreHero({ pct, count, deltaWeek }: { pct: number; count: number; deltaWeek: number }) {
  if (count === 0) {
    return (
      <Link
        to="/notifications"
        style={{
          textDecoration: 'none',
          color: 'var(--bg)',
          background: 'var(--ink)',
          borderRadius: 28,
          padding: '24px 28px',
          display: 'flex',
          alignItems: 'center',
          gap: 24,
        }}
      >
        <div style={{ flex: 1 }}>
          <Eyebrow tone="coral" style={{ marginBottom: 6 }}>No ratings yet</Eyebrow>
          <div
            className="serif"
            style={{ fontSize: 28, lineHeight: 1.05, fontWeight: 500, fontStyle: 'italic', color: 'var(--bg)' }}
          >
            Share your link to get the first one.
          </div>
        </div>
      </Link>
    );
  }
  return (
    <Link
      to="/ratings"
      className="home-score-hero"
      style={{
        textDecoration: 'none',
        color: 'var(--bg)',
        background: 'var(--ink)',
        borderRadius: 28,
        padding: '24px 28px',
        display: 'block',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <div>
        <div
          className="serif home-score-pct"
          style={{
            fontSize: 72,
            lineHeight: 0.9,
            fontWeight: 500,
            fontStyle: 'italic',
            color: 'var(--coral)',
          }}
        >
          {Math.round(pct)}%
        </div>
        <div
          className="mono"
          style={{ fontSize: 11, opacity: 0.7, marginTop: 8, letterSpacing: 1, textTransform: 'uppercase' }}
        >
          Would date · {count} rating{count === 1 ? '' : 's'}
        </div>
        {deltaWeek > 0 && (
          <div
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              marginTop: 10,
              background: 'rgba(255,255,255,0.08)',
              padding: '5px 11px',
              borderRadius: 100,
              fontSize: 11,
            }}
          >
            <Icons.arrow size={11} style={{ color: 'var(--coral)' }} />
            <span><span style={{ color: 'var(--coral)' }}>+{deltaWeek}</span> this week</span>
          </div>
        )}
      </div>
      {/* Ring chart removed — the big italic % above already says it.
          The dark hero now reads cleaner as a single column. */}
    </Link>
  );
}

// ── CenteredSpinner — full-area loading state for the dashboard ─────
// Used while the user / profile / public snapshot are being fetched.
// Replaces the old PublishPrompt component entirely — unpublished
// profiles are redirected to /builder instead.
function CenteredSpinner() {
  return (
    <div style={{ minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <Spinner size={28} color="var(--ink-3)" />
    </div>
  );
}

// ── Share-card preview tile ─────────────────────────────────────────
// Shareable card with an overlay Share control. The Share button is a
// VIEWER-only chrome — it lives outside the bordered card area so the
// PNG snapshot (captured below from the inner cardRef) does NOT
// include it.
//
// Share flow:
//   1. Click the coral overlay button.
//   2. html-to-image rasterises the card DOM into a PNG blob (3x pixel
//      ratio so it stays sharp on retina).
//   3. If the browser supports Web Share API with files (most mobile
//      browsers + recent macOS Safari), open the OS share sheet with
//      the PNG file. The user picks Instagram / WhatsApp / etc.
//   4. Otherwise, open a fallback modal showing the captured PNG with
//      Download + Copy-link buttons.
function ShareCardPreview({
  slug, publicUrl, userId, initial, photoUrl, displayName,
}: {
  slug: string;
  publicUrl: string;
  userId: string;
  initial: string;
  photoUrl: string | null;
  displayName: string;
}) {
  const cardRef = useRef<HTMLDivElement | null>(null);
  const [sheet, setSheet] = useState<{ dataUrl: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  // Menu state for the floating share button — open shows a small popover
  // with two actions: Copy link + Share card. Click-outside listener
  // below closes it.
  const [menuOpen, setMenuOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  // Close the menu when clicking anywhere outside it.
  useEffect(() => {
    if (!menuOpen) return;
    const onDocClick = (e: MouseEvent) => {
      const target = e.target as Node | null;
      if (!target) return;
      // Click on the button or inside the menu → leave alone.
      if (
        (target instanceof Element) &&
        (target.closest('[data-share-menu]') || target.closest('[data-share-trigger]'))
      ) return;
      setMenuOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [menuOpen]);

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(publicUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch { /* clipboard blocked — silently fail */ }
    setMenuOpen(false);
  };

  // Pre-inlined photo as a base64 data URL. The R2-hosted lead image
  // is cross-origin and the bucket may not return CORS headers, which
  // taints the canvas when html-to-image rasterises the card —
  // rendering the photo slot blank. We fetch via /media/proxy (same
  // origin as the API, host-validated, sends CORS headers) and
  // serialise to a data URL the canvas can consume cleanly.
  const [inlinedPhoto, setInlinedPhoto] = useState<string | null>(null);
  useEffect(() => {
    if (!photoUrl) { setInlinedPhoto(null); return; }
    let cancelled = false;
    (async () => {
      try {
        const proxied = mediaApi.proxyURL(photoUrl);
        const blob = await fetch(proxied, { cache: 'force-cache' }).then((r) => {
          if (!r.ok) throw new Error(`proxy ${r.status}`);
          return r.blob();
        });
        const reader = new FileReader();
        reader.onload = () => {
          if (!cancelled && typeof reader.result === 'string') setInlinedPhoto(reader.result);
        };
        reader.readAsDataURL(blob);
      } catch {
        // Fall back to the raw URL — the captured image will be blank
        // in this slot, but the rest of the card still renders.
        if (!cancelled) setInlinedPhoto(null);
      }
    })();
    return () => { cancelled = true; };
  }, [photoUrl]);

  const filename = `wydm-${slug || displayName.toLowerCase().replace(/\s+/g, '-')}.png`;

  const shareCard = async () => {
    setMenuOpen(false);
    if (!cardRef.current || busy) return;
    setBusy(true);
    setErr(null);
    try {
      const dataUrl = await toPng(cardRef.current, {
        pixelRatio: 3,
        cacheBust: true,
        backgroundColor: '#000', // matches card's dark gradient floor
        // Skip nodes that html-to-image can't faithfully rasterise.
        // Currently no filter is needed; left here as a hook point.
        filter: (node) => !(node instanceof HTMLElement && node.dataset.shareExclude === 'true'),
      });

      // Try the native Web Share sheet first. Needed to convert the
      // dataUrl to a File for navigator.share's `files` payload.
      const blob = await dataUrlToBlob(dataUrl);
      const file = new File([blob], filename, { type: 'image/png' });

      const nav = navigator as Navigator & { canShare?: (data: ShareData) => boolean };
      const sharePayload: ShareData = {
        files: [file],
        title: 'Would you date me?',
        text: `Rate my WYDM card`,
        url: publicUrl,
      };
      if (nav.canShare?.(sharePayload)) {
        await nav.share(sharePayload);
        setBusy(false);
        return;
      }

      // Fallback — open the in-app sheet with the captured image +
      // download + copy-link controls.
      setSheet({ dataUrl });
    } catch (e) {
      // AbortError fires when the user dismisses the OS sheet —
      // not an actual error, swallow.
      const name = (e as { name?: string }).name;
      if (name !== 'AbortError') {
        setErr(friendlyError(e, 'Home'));
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ position: 'relative' }}>
      <div
        ref={cardRef}
        style={{
          aspectRatio: '1/1',
          borderRadius: 24,
          overflow: 'hidden',
          position: 'relative',
          boxShadow: 'var(--shadow-card)',
        }}
      >
        {/* When the photo is available as an inlined data URL, render
            that instead of the R2 URL — html-to-image can't taint the
            canvas with a data:image source, so the captured PNG
            includes the lead photo. Falls back to the raw URL while
            the inlined version is being fetched. */}
        <Photo
          src={inlinedPhoto ?? photoUrl ?? undefined}
          variant={photoVariant(userId)}
          initial={photoUrl ? undefined : initial}
          rounded={24}
        />
        {/* Bottom-up vignette for legible bottom-lockup text. */}
        <div
          style={{
            position: 'absolute',
            inset: 0,
            background:
              'linear-gradient(to top, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0.3) 32%, transparent 60%)',
          }}
        />
        <div style={{ position: 'absolute', top: 14, left: 14, display: 'flex', alignItems: 'center', gap: 4 }}>
          <div className="serif" style={{ fontSize: 14, fontStyle: 'italic', color: '#fff', fontWeight: 500 }}>wydm</div>
          <div style={{ width: 4, height: 4, borderRadius: 4, background: 'var(--coral)' }} />
        </div>
        {/* QR tile, top-right — encoded public URL. */}
        <div
          style={{
            position: 'absolute',
            top: 12,
            right: 12,
            background: '#fff',
            borderRadius: 8,
            padding: 4,
            boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
            display: 'flex',
          }}
          aria-label={`QR code linking to ${publicUrl}`}
        >
          <QRCodeSVG value={publicUrl} size={40} level="M" fgColor="#1a1a1a" bgColor="#ffffff" />
        </div>
        {/* Right inset = 110 to clear the desktop action cluster
            (eye + share buttons at right:14). On mobile the floating
            share button sits outside the card so this padding only
            steals harmless slack from the bottom-lockup text. */}
        <div style={{ position: 'absolute', bottom: 14, left: 14, right: 110, color: '#fff' }}>
          <div className="serif" style={{ fontSize: 22, fontWeight: 500, lineHeight: 1.05 }}>
            Would you <span style={{ fontStyle: 'italic' }}>date me</span>?
          </div>
          <div
            className="mono"
            style={{ fontSize: 10, opacity: 0.85, marginTop: 6, display: 'flex', alignItems: 'center', gap: 4 }}
          >
            <Icons.lock size={9} /> {`wydm.app/p/${slug}`}
          </div>
        </div>
      </div>

      {/* Action overlay — eye + share sit visually INSIDE the card's
          bottom-right corner, but as siblings of the cardRef div so
          html-to-image's capture (toPng on cardRef.current) excludes
          them automatically. Same layout on mobile + desktop. */}
      <div
        style={{
          position: 'absolute',
          right: 14,
          bottom: 14,
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          zIndex: 2,
        }}
      >
        <a
          href={publicUrl}
          target="_blank"
          rel="noreferrer"
          title="View public profile"
          aria-label="View public profile"
          style={{
            width: 40,
            height: 40,
            borderRadius: 100,
            background: 'rgba(255,255,255,0.92)',
            color: 'var(--ink)',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            backdropFilter: 'blur(6px)',
            WebkitBackdropFilter: 'blur(6px)',
            boxShadow: '0 2px 8px rgba(0,0,0,0.18)',
            textDecoration: 'none',
          }}
        >
          <Icons.eye size={16} />
        </a>
        <button
          type="button"
          onClick={() => setMenuOpen((v) => !v)}
          disabled={busy}
          data-share-trigger
          aria-haspopup="menu"
          aria-expanded={menuOpen}
          aria-label="Share"
          title="Share"
          style={{
            width: 40,
            height: 40,
            borderRadius: 100,
            background: 'var(--coral)',
            color: '#fff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            border: 'none',
            boxShadow: '0 2px 8px rgba(232,106,76,0.4)',
            cursor: busy ? 'wait' : 'pointer',
            opacity: busy ? 0.7 : 1,
          }}
        >
          {busy ? <Spinner size={16} color="#fff" /> : <Icons.share size={16} />}
        </button>
      </div>

      {/* Mini context menu — two actions stacked. Anchors above the
          share button cluster (right:14, bottom:14 + 40h). */}
      {menuOpen && (
        <div
          role="menu"
          data-share-menu
          style={{
            position: 'absolute',
            right: 14,
            bottom: 64,
            minWidth: 184,
            background: 'var(--surface)',
            borderRadius: 14,
            boxShadow: 'var(--shadow-lift)',
            padding: 6,
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            zIndex: 50,
            animation: 'scale-in 0.12s ease',
            transformOrigin: '100% 100%',
          }}
        >
          <ShareMenuItem icon={<Icons.copy size={15} />} onClick={copyLink}>
            {copied ? 'Link copied' : 'Copy link'}
          </ShareMenuItem>
          <ShareMenuItem icon={<Icons.share size={15} />} onClick={shareCard}>
            Share card
          </ShareMenuItem>
        </div>
      )}

      {err && (
        <div style={{
          marginTop: 14,
          padding: '8px 12px',
          background: 'rgba(208,64,48,0.08)',
          color: '#a8331f',
          borderRadius: 10,
          fontSize: 12,
        }}>
          {err}
        </div>
      )}

      {sheet && (
        <CardShareSheet
          dataUrl={sheet.dataUrl}
          filename={filename}
          publicUrl={publicUrl}
          onClose={() => setSheet(null)}
        />
      )}
    </div>
  );
}

// CardShareSheet — fallback modal when Web Share API with files isn't
// available (eg. desktop Chrome / Firefox). Shows the captured PNG +
// Download + Copy-link controls.
function CardShareSheet({
  dataUrl, filename, publicUrl, onClose,
}: { dataUrl: string; filename: string; publicUrl: string; onClose: () => void }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(publicUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch { /* noop */ }
  };
  return (
    <div
      role="dialog"
      aria-modal="true"
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(43,29,20,0.55)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 100, padding: 20, animation: 'fade-in 0.2s ease',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'var(--surface)', borderRadius: 24, padding: 24,
          width: '100%', maxWidth: 380, boxShadow: 'var(--shadow-lift)',
          animation: 'scale-in 0.2s ease',
        }}
      >
        <Eyebrow tone="coral" style={{ marginBottom: 8 }}>Share your card</Eyebrow>
        <div className="serif" style={{ fontSize: 22, fontWeight: 500, fontStyle: 'italic', marginBottom: 14 }}>
          Save the image or copy the link.
        </div>
        <img
          src={dataUrl}
          alt="Your share card"
          style={{
            width: '100%',
            aspectRatio: '1/1',
            objectFit: 'cover',
            borderRadius: 16,
            display: 'block',
            marginBottom: 14,
          }}
        />
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <a
            href={dataUrl}
            download={filename}
            style={{ textDecoration: 'none' }}
          >
            <Btn variant="primary" size="md" full icon={<Icons.share size={14} />}>
              Download image
            </Btn>
          </a>
          <Btn variant="cream" size="md" full icon={<Icons.copy size={14} />} onClick={copy}>
            {copied ? 'Link copied' : 'Copy link'}
          </Btn>
          <Btn variant="ghost" size="md" full onClick={onClose}>Close</Btn>
        </div>
      </div>
    </div>
  );
}

// ShareMenuItem — one row in the share button's context menu. Icon +
// label, full-width inside the menu, coral hover.
function ShareMenuItem({
  icon, onClick, children,
}: { icon: React.ReactNode; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      type="button"
      role="menuitem"
      onClick={onClick}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: '10px 12px',
        borderRadius: 10,
        background: 'transparent',
        color: 'var(--ink)',
        fontSize: 13,
        fontWeight: 500,
        textAlign: 'left',
        border: 'none',
        cursor: 'pointer',
        fontFamily: 'var(--font-ui)',
      }}
      onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--bg)'; }}
      onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
    >
      <span style={{ color: 'var(--ink-3)', display: 'flex', alignItems: 'center' }}>{icon}</span>
      {children}
    </button>
  );
}

async function dataUrlToBlob(dataUrl: string): Promise<Blob> {
  // dataUrl is data:image/png;base64,<...>; fetch() handles the decode.
  const res = await fetch(dataUrl);
  return res.blob();
}

// ── Premium pitch (right column, only when free) ─────────────────────
//
// onUpgrade fires the parent's PaywallSheet (desktop modal / mobile
// sheet, both with the "subscribe in the app" callout). Used to be a
// <Link> to /settings/subscription back when that page existed.
function PremiumPitch({ onUpgrade }: { onUpgrade: () => void }) {
  return (
    <button
      type="button"
      onClick={onUpgrade}
      style={{
        textAlign: 'left',
        border: 'none',
        cursor: 'pointer',
        color: 'var(--bg)',
        padding: 22,
        borderRadius: 24,
        background: 'var(--ink)',
        display: 'block',
        width: '100%',
        fontFamily: 'var(--font-ui)',
      }}
    >
      <Eyebrow tone="coral" style={{ marginBottom: 8 }}>WYDM Premium</Eyebrow>
      <div className="serif" style={{ fontSize: 26, fontStyle: 'italic', fontWeight: 500, lineHeight: 1.1 }}>
        DMs. AI Reviews.
      </div>
      <div style={{ fontSize: 13, opacity: 0.7, marginTop: 8, lineHeight: 1.5 }}>
        The two things free WYDM doesn't have.
      </div>
      <div
        style={{
          marginTop: 14,
          padding: '10px 16px',
          borderRadius: 100,
          background: 'var(--coral)',
          color: '#fff',
          fontSize: 13,
          fontWeight: 600,
          display: 'inline-block',
        }}
      >
        Get premium →
      </div>
    </button>
  );
}

// ── helpers ──────────────────────────────────────────────────────────
function isWithinDays(days: number) {
  const cutoff = Date.now() - days * 24 * 60 * 60 * 1000;
  return (r: RatingsDashboard['recent'][number]) => new Date(r.created_at).getTime() >= cutoff;
}

function capitalize(s: string): string { return s.charAt(0).toUpperCase() + s.slice(1); }
